<script setup lang="ts">
import { BarChart } from '@/components/ui/chart-bar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Banknote, HardDrive, ShoppingCart, Wallet, Handshake } from 'lucide-vue-next';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { computed, ref } from 'vue';
import type { DateRangeValueType } from '@lib/zod/dateTimePropEditor';
import type { ResType } from '@api-client/typeUtils';
import type { dashboardApiClient } from '@api-client/dashboard';
import PartnerEntityDailyChart from '@partner-spa-vue/components/PartnerEntityDailyChart.vue';
import { fillDailyGaps, rollupWeekly } from '@partner-spa-vue/components/partnerReportChartUtils';

type CurrencyReport = ResType<typeof dashboardApiClient.website.partner_marketing.store_reports[':websiteId']['$post']>['data']['reports'][number];

const props = defineProps<{
    report: CurrencyReport;
    partnerType: 'marketing' | 'support';
    dateRange: DateRangeValueType;
    granularity: 'daily' | 'weekly';
}>();

const openStoreIds = ref<string[]>([]);
const openDeliveryteamIds = ref<string[]>([]);

const isMarketing = computed(() => props.partnerType === 'marketing');

const dailyChartData = computed(() =>
    fillDailyGaps(
        props.report.daily,
        props.dateRange.start ?? 0,
        props.dateRange.end ?? Date.now(),
    ),
);

const chartData = computed(() =>
    props.granularity === 'weekly'
        ? rollupWeekly(dailyChartData.value)
        : dailyChartData.value,
);

const periodLabel = computed(() =>
    props.granularity === 'weekly' ? 'Weekly' : 'Daily',
);

const totalOrders = computed(() =>
    props.report.byStore.reduce((acc, row) => acc + row.count, 0)
    + props.report.byDeliveryteam.reduce((acc, row) => acc + row.count, 0),
);

const totalGmv = computed(() =>
    props.report.byStore.reduce((acc, row) => acc + row.total, 0)
    + props.report.byDeliveryteam.reduce((acc, row) => acc + row.total, 0),
);

const totalEarnings = computed(() =>
    props.report.byStore.reduce((acc, row) => acc + row.earnings, 0)
    + props.report.byDeliveryteam.reduce((acc, row) => acc + row.earnings, 0),
);

const totalEarningsUnpaid = computed(() =>
    props.report.byStore.reduce((acc, row) => acc + row.earningsUnpaid, 0)
    + props.report.byDeliveryteam.reduce((acc, row) => acc + row.earningsUnpaid, 0),
);

const totalPlatformFees = computed(() =>
    props.report.byStore.reduce((acc, row) => acc + row.platformFees, 0)
    + props.report.byDeliveryteam.reduce((acc, row) => acc + row.platformFees, 0),
);

const totalPlatformFeesUnpaid = computed(() =>
    props.report.byStore.reduce((acc, row) => acc + row.platformFeesUnpaid, 0)
    + props.report.byDeliveryteam.reduce((acc, row) => acc + row.platformFeesUnpaid, 0),
);

const totalSupportFees = computed(() =>
    props.report.byStore.reduce((acc, row) => acc + row.supportFees, 0)
    + props.report.byDeliveryteam.reduce((acc, row) => acc + row.supportFees, 0),
);

const totalSupportFeesUnpaid = computed(() =>
    props.report.byStore.reduce((acc, row) => acc + row.supportFeesUnpaid, 0)
    + props.report.byDeliveryteam.reduce((acc, row) => acc + row.supportFeesUnpaid, 0),
);

const currencyLabel = computed(() => props.report.currency.toUpperCase());
</script>

<template>
    <div class="space-y-4 border rounded-lg p-4">
        <h3 class="text-lg font-semibold">
            {{ currencyLabel }}
        </h3>

        <div class="grid gap-4 grid-cols-2">
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Total Orders
                    </CardTitle>
                    <ShoppingCart class="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ totalOrders }}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Total GMV
                    </CardTitle>
                    <Banknote class="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ formatCurrency(totalGmv, report.currency) }}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Your earnings
                    </CardTitle>
                    <Wallet class="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ formatCurrency(totalEarnings, report.currency) }}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Your earnings(unpaid)
                    </CardTitle>
                    <Wallet class="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ formatCurrency(totalEarningsUnpaid, report.currency) }}
                    </div>
                </CardContent>
            </Card>
            <Card v-if="isMarketing">
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Support fees
                    </CardTitle>
                    <Handshake class="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ formatCurrency(totalSupportFees, report.currency) }}
                    </div>
                </CardContent>
            </Card>
            <Card v-if="isMarketing">
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Support fees(unpaid)
                    </CardTitle>
                    <Handshake class="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ formatCurrency(totalSupportFeesUnpaid, report.currency) }}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        SMS/Hosting fees
                    </CardTitle>
                    <HardDrive class="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ formatCurrency(totalPlatformFees, report.currency) }}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        SMS/Hosting fees(unpaid)
                    </CardTitle>
                    <HardDrive class="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ formatCurrency(totalPlatformFeesUnpaid, report.currency) }}
                    </div>
                </CardContent>
            </Card>
        </div>

        <div class="space-y-4"
             v-if="chartData.length > 0">
            <div class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} orders
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, count: row.count }))"
                          index="date"
                          class="h-[140px]"
                          :categories="['count']"
                          :rounded-corners="1" />
            </div>
            <div class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} GMV
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, total: row.total }))"
                          index="date"
                          class="h-[140px]"
                          :categories="['total']"
                          :rounded-corners="1" />
            </div>
            <div class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} earnings
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, earnings: row.earnings }))"
                          index="date"
                          class="h-[140px]"
                          :categories="['earnings']"
                          :rounded-corners="1" />
            </div>
            <div v-if="isMarketing"
                 class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} support fees
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, supportFees: row.supportFees }))"
                          index="date"
                          class="h-[140px]"
                          :categories="['supportFees']"
                          :rounded-corners="1" />
            </div>
            <div class="p-4 border rounded-md">
                <p class="text-sm font-medium mb-2">
                    {{ periodLabel }} SMS/Hosting fees
                </p>
                <BarChart :data="chartData.map(row => ({ date: row.date, platformFees: row.platformFees }))"
                          index="date"
                          class="h-[140px]"
                          :categories="['platformFees']"
                          :rounded-corners="1" />
            </div>
        </div>

        <Card>
            <CardHeader>
                <CardTitle class="text-base">
                    By store
                </CardTitle>
                <p class="text-sm text-muted-foreground">
                    Expand a row to see charts
                </p>
            </CardHeader>
            <CardContent>
                <p v-if="report.byStore.length === 0"
                   class="text-muted-foreground text-center text-sm py-4">
                    No store orders in this range
                </p>
                <template v-else>
                    <Accordion v-model="openStoreIds"
                               type="multiple"
                               class="w-full">
                        <AccordionItem v-for="row in report.byStore"
                                       :key="row.storeId"
                                       :value="row.storeId">
                            <AccordionTrigger class="hover:no-underline py-3">
                                <div class="w-full space-y-1 pr-2 text-left">
                                    <div class="flex items-center gap-4">
                                        <span class="font-medium truncate flex-1 min-w-0">
                                            {{ row.storeName }}
                                        </span>
                                        <span class="tabular-nums shrink-0">
                                            Count: {{ row.count }}
                                        </span>
                                        <span class="tabular-nums shrink-0 w-40 text-right">
                                            GMV: {{ formatCurrency(row.total, report.currency) }}
                                        </span>
                                    </div>
                                    <div class="text-xs text-muted-foreground w-full flex justify-end">
                                        <table class="max-w-fit">
                                            <tbody>
                                                <tr>
                                                    <td class="text-right pr-2">Your earnings:</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.earnings, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-right pr-2">Your earnings(unpaid):</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.earningsUnpaid, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr v-if="isMarketing">
                                                    <td class="text-right pr-2">Support:</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.supportFees, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr v-if="isMarketing">
                                                    <td class="text-right pr-2">Support(unpaid):</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.supportFeesUnpaid, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-right pr-2">SMS/Hosting:</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.platformFees, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-right pr-2">SMS/Hosting(unpaid):</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.platformFeesUnpaid, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-right font-medium pr-2">Total:</td>
                                                    <td class="tabular-nums text-right font-medium">
                                                        <template v-if="isMarketing">
                                                            {{ formatCurrency(row.platformFees + row.supportFees + row.earnings, report.currency) }}
                                                        </template>
                                                        <template v-else>
                                                            {{ formatCurrency(row.platformFees + row.earnings, report.currency) }}
                                                        </template>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <PartnerEntityDailyChart v-if="openStoreIds.includes(row.storeId)"
                                                         :partner-type="partnerType"
                                                         :date-range="dateRange"
                                                         :granularity="granularity"
                                                         :currency="report.currency"
                                                         :store-id="row.storeId" />
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </template>
            </CardContent>
        </Card>

        <Card>
            <CardHeader>
                <CardTitle class="text-base">
                    By delivery team
                </CardTitle>
                <p class="text-sm text-muted-foreground">
                    Expand a row to see charts
                </p>
            </CardHeader>
            <CardContent>
                <p v-if="report.byDeliveryteam.length === 0"
                   class="text-muted-foreground text-center text-sm py-4">
                    No delivery team orders in this range
                </p>
                <template v-else>
                    <Accordion v-model="openDeliveryteamIds"
                               type="multiple"
                               class="w-full">
                        <AccordionItem v-for="row in report.byDeliveryteam"
                                       :key="row.deliveryteamId"
                                       :value="row.deliveryteamId">
                            <AccordionTrigger class="hover:no-underline py-3">
                                <div class="w-full space-y-1 pr-2 text-left">
                                    <div class="flex items-center gap-4">
                                        <span class="font-medium truncate flex-1 min-w-0">
                                            {{ row.deliveryteamName }}
                                        </span>
                                        <span class="tabular-nums shrink-0">
                                            {{ row.count }}
                                        </span>
                                        <span class="tabular-nums shrink-0 w-24 text-right">
                                            {{ formatCurrency(row.total, report.currency) }}
                                        </span>
                                    </div>
                                    <div class="text-xs text-muted-foreground w-full flex justify-end">
                                        <table class="max-w-fit">
                                            <tbody>
                                                <tr>
                                                    <td class="text-right pr-2">Your earnings:</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.earnings, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-right pr-2">Your earnings(unpaid):</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.earningsUnpaid, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr v-if="isMarketing">
                                                    <td class="text-right pr-2">Support:</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.supportFees, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr v-if="isMarketing">
                                                    <td class="text-right pr-2">Support(unpaid):</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.supportFeesUnpaid, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-right pr-2">SMS/Hosting:</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.platformFees, report.currency) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-right pr-2">SMS/Hosting(unpaid):</td>
                                                    <td class="tabular-nums text-right">
                                                        {{ formatCurrency(row.platformFeesUnpaid, report.currency) }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <PartnerEntityDailyChart v-if="openDeliveryteamIds.includes(row.deliveryteamId)"
                                                         :partner-type="partnerType"
                                                         :date-range="dateRange"
                                                         :granularity="granularity"
                                                         :currency="report.currency"
                                                         :deliveryteam-id="row.deliveryteamId" />
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </template>
            </CardContent>
        </Card>
    </div>
</template>
